import type { MouseEvent, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

interface RouterNavLinkProps {
  to: string;
  end?: boolean;
  className?: string | ((args: { isActive: boolean }) => string);
  onNavigate?: () => void;
  children: ReactNode;
}

function normalizePath(path: string): string {
  return path.replace(/\/+$/, "") || "/";
}

function isPathActive(pathname: string, to: string, end?: boolean): boolean {
  const current = normalizePath(pathname);
  const target = normalizePath(to);
  if (end) {
    return current === target;
  }
  return current === target || current.startsWith(`${target}/`);
}

/**
 * Sidebar / document nav link — uses React Router `Link` for reliable SPA navigation.
 * (Ctrl+click still opens a new tab; plain click uses client-side routing.)
 */
export function RouterNavLink({
  to,
  end,
  className,
  onNavigate,
  children,
}: RouterNavLinkProps) {
  const { pathname } = useLocation();
  const isActive = isPathActive(pathname, to, end);

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    onNavigate?.();
  }

  const resolvedClassName =
    typeof className === "function" ? className({ isActive }) : className;

  return (
    <Link
      to={to}
      onClick={handleClick}
      aria-current={isActive ? "page" : undefined}
      className={resolvedClassName}
    >
      {children}
    </Link>
  );
}
